import { lazy } from 'react';
import { Navigate } from 'react-router';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import PrivateRoute from './utils/PrivateRoute';
import { CORE_PERMISSIONS } from '@a1/shared';
import SentryErrorBoundary from '../SentryErrorBoundary';

// Dashboard
const DashboardBookingStats = Loadable(lazy(() => import('views/shared/dashboard-booking-stats')));
const DashboardSurveyStats = Loadable(lazy(() => import('views/shared/dashboard-survey-stats')));

// Manage Admins
const ManageAdmins = Loadable(lazy(() => import('views/admin/manage-admins')));
const CreateAdminAccount = Loadable(lazy(() => import('views/admin/manage-admins/create-account')));
const EditAdminAccount = Loadable(lazy(() => import('views/admin/manage-admins/edit-account')));

// Manage Managers
const ManageManagers = Loadable(lazy(() => import('views/admin/manage-managers')));
const CreateManager = Loadable(lazy(() => import('views/admin/manage-managers/Create')));
const EditManager = Loadable(lazy(() => import('views/admin/manage-managers/Edit')));

// Manage External Users
const ManageExternalUsers = Loadable(lazy(() => import('views/admin/manage-external-users')));
const CreateExternalUserAccount = Loadable(lazy(() => import('views/admin/manage-external-users/create-account')));
const PublishAnnouncementExternalUser = Loadable(lazy(() => import('views/admin/manage-external-users/publish-announcements')));
const EditExternalUserAccount = Loadable(lazy(() => import('views/admin/manage-external-users/edit-account')));

// Manage Respondents
const ManageRespondents = Loadable(lazy(() => import('views/admin/respondents')));
const CreateRespondent = Loadable(lazy(() => import('views/shared/respondent/create-respondent')));
const EditRespondent = Loadable(lazy(() => import('views/shared/respondent/edit-respondent')));
const UploadRespondentList = Loadable(lazy(() => import('views/admin/respondents/upload-respondent-list')));
const ManageStatuses = Loadable(lazy(() => import('views/admin/respondents/manage-statuses')));
const ViewRespondent = Loadable(lazy(() => import('views/shared/respondent')));

// View Surveys
const ViewSurveys = Loadable(lazy(() => import('views/admin/view-surveys')));
const ViewSurvey = Loadable(lazy(() => import('views/admin/view-survey')));
const ViewRespondentSurvey = Loadable(lazy(() => import('views/shared/view-respondent-survey')));
const AnswerRespondentSurvey = Loadable(lazy(() => import('views/shared/answer-respondent-survey')));

// Manage Survey Types
const ManageSurveyTypes = Loadable(lazy(() => import('views/admin/manage-survey-types')));
const EditSurveyType = Loadable(lazy(() => import('views/admin/manage-survey-types/edit-survey-type')));
const ViewSurveyType = Loadable(lazy(() => import('views/admin/manage-survey-types/view-survey-type')));
const ManageSurveyTypeTextTranslations = Loadable(lazy(() => import('views/admin/manage-survey-type-text-translations')));

// Manage Survey Backgrounds
const ManageSurveyBackgrounds = Loadable(lazy(() => import('views/admin/manage-survey-backgrounds')));

// Manage Access Roles
const ManageAccessRoles = Loadable(lazy(() => import('views/admin/manage-access-roles')));
const CreateAccessRole = Loadable(lazy(() => import('views/admin/manage-access-roles/create-access-role')));
const EditAccessRole = Loadable(lazy(() => import('views/admin/manage-access-roles/edit-access-role')));

// Manage Survey Reports
const ManageSurveyReports = Loadable(lazy(() => import('views/admin/manage-survey-reports')));

// Manage Recipients
const ManageRecipients = Loadable(lazy(() => import('views/admin/manage-recipients')));
const AddRecipient = Loadable(lazy(() => import('views/admin/manage-recipients/add-recipient')));
const EditRecipient = Loadable(lazy(() => import('views/admin/manage-recipients/edit-recipient')));
const ImportCSV = Loadable(lazy(() => import('views/admin/manage-recipients/import-csv')));
const ManageTags = Loadable(lazy(() => import('views/admin/manage-recipients/manage-tags')));

// Manage App Configuration
const ManageAppConfigurations = Loadable(lazy(() => import('views/admin/manage-app-configurations')));

// Manage API
const ManageApi = Loadable(lazy(() => import('views/admin/manage-api')));
const ManageWebhooks = Loadable(lazy(() => import('views/admin/manage-webhooks')));

// Manage Integration
const ManageIntegration = Loadable(lazy(() => import('views/admin/manage-integrations')));
const ManageIinsightIntegration = Loadable(lazy(() => import('views/admin/manage-integrations/iinsight')));
const CreateIinsightIntegration = Loadable(lazy(() => import('views/admin/manage-integrations/iinsight/create-integration')));
const SploseIntegration = Loadable(lazy(() => import('../views/admin/manage-integrations/splose')));
const GoogleCalendarIntegration = Loadable(lazy(() => import('views/admin/manage-integrations/google-calendar')));
const OutlookCalendarIntegration = Loadable(lazy(() => import('views/admin/manage-integrations/outlook-calendar')));
const OutlookMailIntegration = Loadable(lazy(() => import('views/admin/manage-integrations/outlook-mail')));
const D365FinanceIntegration = Loadable(lazy(() => import('views/admin/manage-integrations/d365-finance')));

// Manage Billing
const ManageBilling = Loadable(lazy(() => import('views/admin/manage-billing')));
const CreateBillingAccount = Loadable(lazy(() => import('views/admin/manage-billing/create-billing-account')));
const RechargeBalance = Loadable(lazy(() => import('views/admin/manage-billing/recharge-balance')));
const BillingAccountSettings = Loadable(lazy(() => import('views/admin/manage-billing/billing-account-settings')));

// SalesAccount
const SalesAccount = Loadable(lazy(() => import('views/admin/sales-account')));

// Manage Services
const ManageServices = Loadable(lazy(() => import('views/admin/manage-services')));
const CreateService = Loadable(lazy(() => import('views/admin/manage-services/create-service')));
const EditService = Loadable(lazy(() => import('views/admin/manage-services/edit-service')));

// Manage Locations
const ManageLocations = Loadable(lazy(() => import('views/admin/manage-locations')));
const Locations = Loadable(lazy(() => import('views/admin/manage-locations')));
const CreateLocation = Loadable(lazy(() => import('views/admin/manage-locations/Create')));
const EditLocation = Loadable(lazy(() => import('views/admin/manage-locations/Edit')));

// Manage Booking Pages
const ManageBookingPages = Loadable(lazy(() => import('views/admin/manage-booking-pages')));
const CreateBookingPage = Loadable(lazy(() => import('views/admin/manage-booking-pages/create-booking-page')));
const EditBookingPage = Loadable(lazy(() => import('views/admin/manage-booking-pages/edit-booking-page')));
const CreateBookingFromBookingPage = Loadable(lazy(() => import('views/admin/manage-booking-pages/create-booking')));

// Manage Bookings
const ManageBookings = Loadable(lazy(() => import('views/admin/manage-bookings')));

// Communications
const ManageCommunications = Loadable(lazy(() => import('views/admin/manage-communications')));
const ViewCommunications = Loadable(lazy(() => import('views/admin/manage-communications/ViewCommunications')));

// Manage Bookings - Calendar
const ManageBookingCalendar = Loadable(lazy(() => import('views/admin/manage-booking-calendar')));

// Manage Bookings - Calendar
const DownloadBookingDocument = Loadable(lazy(() => import('views/admin/download-booking-document')));

// Manage Companies
const ManageCompanies = Loadable(lazy(() => import('views/admin/manage-companies')));
const CreateCompany = Loadable(lazy(() => import('views/admin/manage-companies/create-company')));
const EditCompany = Loadable(lazy(() => import('views/admin/manage-companies/edit-company')));
const CompanyBillingProfile = Loadable(lazy(() => import('views/admin/manage-companies/billing-profile')));

// Manage Tax Rates
const ManageTaxRates = Loadable(lazy(() => import('views/admin/manage-tax-rates')));
// Manage Items
const ManageItems = Loadable(lazy(() => import('views/admin/manage-items')));
// Manage Invoices
const ManageInvoices = Loadable(lazy(() => import('views/shared/manage-invoices')));
const EditInvoice = Loadable(lazy(() => import('views/shared/manage-invoices/edit-invoice')));
// Manage Contacts
const ManageContacts = Loadable(lazy(() => import('../views/admin/manage-contacts')));
const CreateContact = Loadable(lazy(() => import('views/admin/manage-contacts/create-contact')));
const EditContact = Loadable(lazy(() => import('views/admin/manage-contacts/edit-contact')));
// Manage Invoice Credit Notes
const EditCreditNote = Loadable(lazy(() => import('views/shared/manage-invoices/credit-notes/edit')));
const ManageCreditNotes = Loadable(lazy(() => import('views/shared/manage-invoices/credit-notes')));
// Workflows
const Workflows = Loadable(lazy(() => import('views/admin/workflows')));
const EditWorkflow = Loadable(lazy(() => import('views/admin/workflows/Edit')));

// Manage Templates
const ManageTemplates = Loadable(lazy(() => import('views/admin/manage-templates')));
const CreateTemplate = Loadable(lazy(() => import('views/admin/manage-templates/create-template')));
const EditTemplate = Loadable(lazy(() => import('views/admin/manage-templates/edit-template')));

// Invoice Export Mapping
const InvoiceExportMapping = Loadable(lazy(() => import('views/admin/invoice-export-mapping')));
const InvoiceExportMappingEdit = Loadable(lazy(() => import('views/admin/invoice-export-mapping/edit')));

// ==============================|| ADMIN ROUTING ||============================== //

const NotificationSettings = Loadable(lazy(() => import('views/admin/notification-settings')));

const AdminRoutes = {
  path: '/',
  element: <MainLayout />,
  errorElement: <SentryErrorBoundary />,
  children: [
    {
      path: '/',
      element: <Navigate to="/dashboard-booking-stats" />
    },
    {
      path: '/dashboard-booking-stats',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_BOOKING_DASHBOARD}>
          <DashboardBookingStats />
        </PrivateRoute>
      )
    },
    {
      path: '/dashboard-survey-stats',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_SURVEY_DASHBOARD}>
          <DashboardSurveyStats />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-admins',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_ADMINISTRATORS}>
          <ManageAdmins />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-admins/create-account',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_ADMINISTRATORS}>
          <CreateAdminAccount />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-admins/edit-account/:adminID',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_ADMINISTRATORS}>
          <EditAdminAccount />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-managers',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_MANAGERS}>
          <ManageManagers />
        </PrivateRoute>
      )
    },
    {
      path: '/managers/create',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_MANAGERS}>
          <CreateManager />
        </PrivateRoute>
      )
    },
    {
      path: '/managers/:managerId/edit',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_MANAGERS}>
          <EditManager />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-ext-users',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_MANAGERS}>
          <ManageExternalUsers />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-ext-users/announcement',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_MANAGERS}>
          <PublishAnnouncementExternalUser />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-ext-users/create-account',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_MANAGERS}>
          <CreateExternalUserAccount />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-ext-users/edit-account/:managerID',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_MANAGERS}>
          <EditExternalUserAccount />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RESPONDENTS}>
          <ManageRespondents />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/create-respondent',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RESPONDENTS}>
          <CreateRespondent />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/edit-respondent/:respondentId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RESPONDENTS}>
          <EditRespondent />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/upload-respondent-list',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RESPONDENTS}>
          <UploadRespondentList />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/manage-statuses',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RESPONDENTS}>
          <ManageStatuses />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/view-respondent/:respondentId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RESPONDENTS}>
          <ViewRespondent />
        </PrivateRoute>
      )
    },
    {
      path: '/view-surveys',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SURVEY_TYPES}>
          <ViewSurveys />
        </PrivateRoute>
      )
    },
    {
      path: '/view-survey/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SURVEY_TYPES}>
          <ViewSurvey />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/view-respondent-survey/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RESPONDENTS}>
          <ViewRespondentSurvey />
        </PrivateRoute>
      )
    },
    {
      path: '/respondents/answer-respondent-survey/:surveyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RESPONDENTS}>
          <AnswerRespondentSurvey />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-survey-types',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SURVEY_TYPES}>
          <ManageSurveyTypes />
        </PrivateRoute>
      )
    },
    {
      path: 'manage-survey-types/edit-survey-type/:surveyTypeId/survey-type-version/:surveyTypeVersionId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SURVEY_TYPES}>
          <EditSurveyType />
        </PrivateRoute>
      )
    },
    {
      path: 'manage-survey-types/view-survey-type/:surveyTypeId/survey-type-version/:surveyTypeVersionId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SURVEY_TYPES}>
          <ViewSurveyType />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-survey-type-text-translations',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SURVEY_TYPES}>
          <ManageSurveyTypeTextTranslations />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-access-roles',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_ACCESS_ROLES}>
          <ManageAccessRoles />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-access-roles/create-access-role/:accessRoleUserType',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_ACCESS_ROLES}>
          <CreateAccessRole />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-access-roles/edit-access-role/:accessRoleId/:accessRoleUserType',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_ACCESS_ROLES}>
          <EditAccessRole />
        </PrivateRoute>
      )
    },
    {
      path: '/admin-manage-survey-backgrounds',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SURVEY_BACKGROUNDS}>
          <ManageSurveyBackgrounds />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-survey-reports',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_REPORTS}>
          <ManageSurveyReports />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-survey-communications',
      element: <Navigate to="/manage-communications/survey" />
    },
    {
      path: '/manage-recipients',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RECIPIENTS}>
          <ManageRecipients />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-recipients/add-recipient',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RECIPIENTS}>
          <AddRecipient />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-recipients/edit-recipient/:recipientId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RECIPIENTS}>
          <EditRecipient />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-recipients/import-csv',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RECIPIENTS}>
          <ImportCSV />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-recipients/manage-tags',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_RECIPIENTS}>
          <ManageTags />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-app-configurations',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <ManageAppConfigurations />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-api',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_API}>
          <ManageApi />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-webhooks',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_API}>
          <ManageWebhooks />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-integrations',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <ManageIntegration />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-integrations/iinsight',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <ManageIinsightIntegration />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-integrations/iinsight/create-integration',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <CreateIinsightIntegration />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-integrations/splose',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <SploseIntegration />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-integrations/google-calendar',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <GoogleCalendarIntegration />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-integrations/outlook-calendar',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <OutlookCalendarIntegration />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-integrations/outlook-mail',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <OutlookMailIntegration />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-integrations/d365-finance',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <D365FinanceIntegration />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-billing',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BILLING}>
          <ManageBilling />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-billing/create-billing-account',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BILLING}>
          <CreateBillingAccount />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-billing/recharge-balance',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BILLING}>
          <RechargeBalance />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-billing/billing-account-settings',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BILLING}>
          <BillingAccountSettings />
        </PrivateRoute>
      )
    },
    // sales account
    {
      path: '/sales-account',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BILLING}>
          <SalesAccount />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-services',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <ManageServices />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-services/create-service',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <CreateService />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-services/edit-service/:serviceId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <EditService />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-locations',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <ManageLocations />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-locations/create-location',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <CreateLocation />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-locations/edit-location/:locationId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <EditLocation />
        </PrivateRoute>
      )
    },
    {
      path: '/locations',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <Locations />
        </PrivateRoute>
      )
    },
    {
      path: '/locations/create',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <CreateLocation />
        </PrivateRoute>
      )
    },
    {
      path: '/locations/:locationId/edit',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <EditLocation />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-pages',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <ManageBookingPages />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-pages/create-booking-page',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <CreateBookingPage />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-pages/edit-booking-page/:bookingPageId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_SERVICES_LOCATIONS_BOOKING_PAGES}>
          <EditBookingPage />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-pages/booking-page/:bookingPageUid/',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS}>
          <CreateBookingFromBookingPage />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-pages/booking-page/:bookingPageUid/preview',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS}>
          <CreateBookingFromBookingPage previewMode />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-bookings',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS}>
          <ManageBookings />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-bookings/:createdBookingId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS}>
          <ManageBookings />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-booking-confirmation-text',
      element: <Navigate to="/manage-communications/booking" />
    },
    {
      path: '/manage-confirmation-reminders',
      element: <Navigate to="/manage-communications/booking" />
    },
    {
      path: '/manage-booking-calendar',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS}>
          <ManageBookingCalendar />
        </PrivateRoute>
      )
    },
    {
      path: '/access-booking-documents/:bookingId/:fileName/download',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS}>
          <DownloadBookingDocument />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-companies',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_COMPANIES}>
          <ManageCompanies />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-companies/create-company',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_COMPANIES}>
          <CreateCompany />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-companies/edit-company/:companyId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_COMPANIES}>
          <EditCompany />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-companies/:companyId/billing-profile',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_COMPANIES} requiresInvoicingAndPayments>
          <CompanyBillingProfile />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-tax-rates',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_TAX_RATES} requiresInvoicingAndPayments>
          <ManageTaxRates />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-items',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_ITEMS} requiresInvoicingAndPayments>
          <ManageItems />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-invoices',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_INVOICES} requiresInvoicingAndPayments>
          <ManageInvoices />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-invoices/:invoiceId/edit',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.EDIT_INVOICE} requiresInvoicingAndPayments>
          <EditInvoice />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-contacts',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS} requiresInvoicingAndPayments>
          <ManageContacts />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-contacts/create-contact',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS} requiresInvoicingAndPayments>
          <CreateContact />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-contacts/edit-contact/:contactId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_BOOKINGS} requiresInvoicingAndPayments>
          <EditContact />
        </PrivateRoute>
      )
    },
    {
      path: '/workflows',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.READ_WORKFLOWS}>
          <Workflows />
        </PrivateRoute>
      )
    },
    {
      path: '/workflows/:workflowId/edit',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.WRITE_WORKFLOWS}>
          <EditWorkflow />
        </PrivateRoute>
      )
    },
    // Todo: credit note permissions
    {
      path: '/manage-invoices/:invoiceId/credit-note/:creditNoteId/edit',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_INVOICES} requiresInvoicingAndPayments>
          <EditCreditNote />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-invoices/:invoiceId/credit-notes',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_INVOICES} requiresInvoicingAndPayments>
          <ManageCreditNotes />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-templates',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_TEMPLATES}>
          <ManageTemplates />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-templates/create-template',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_TEMPLATES}>
          <CreateTemplate />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-templates/edit-template/:templateId',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_TEMPLATES}>
          <EditTemplate />
        </PrivateRoute>
      )
    },
    {
      path: '/invoice-export-mappings',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_INVOICES} requiresInvoicingAndPayments>
          <InvoiceExportMapping />
        </PrivateRoute>
      )
    },
    {
      path: '/invoice-export-mappings/:invoiceExportMappingId/edit',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.VIEW_INVOICES} requiresInvoicingAndPayments>
          <InvoiceExportMappingEdit />
        </PrivateRoute>
      )
    },
    {
      path: '/notification-settings',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_APP_CONFIGURATIONS}>
          <NotificationSettings />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-communications/:type',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_CONFIRMATION_TEXT}>
          <ManageCommunications />
        </PrivateRoute>
      )
    },
    {
      path: '/manage-communications/view/:id',
      element: (
        <PrivateRoute role={CORE_PERMISSIONS.MANAGE_CONFIRMATION_TEXT}>
          <ViewCommunications />
        </PrivateRoute>
      )
    }
  ]
};

export default AdminRoutes;
